import type { SopResumoLinhaMes, SopTotaisLinha, TipoLinhaSop } from '@/types/dashboards';
import { produzidoBase } from './equiplexSop';

/**
 * O volume por linha de produto (Fábrica, Grande volume, Pequeno volume e o Green Pack, que é o
 * sopro) do painel S&OP — o número que a diretoria pede todo mês (Attila, 20/10/2026). O Green
 * Pack fica fora da fábrica: o frasco soprado vira produto acabado e contaria duas vezes.
 *
 * A soma por linha vem pronta do backend, mês a mês; aqui só se recorta pelos meses do período,
 * se soma o período e se monta o CSV. O desvio do período usa a mesma base do backend
 * (`programado_base` × `produzido_base`, mês futuro sem base), igual ao KPI da tela.
 */

export const LINHA_FABRICA = 'Fábrica';

export interface ColunaLinha {
  nome: string;
  tipo: TipoLinhaSop;
}

export interface TotalLinha {
  linha: string;
  tipo: TipoLinhaSop;
  /** Todas as OPs do período (no mês aberto, inclusive as dos dias ainda abertos). */
  produzido: number;
  produzidoBase: number;
  programado: number;
  programadoBase: number;
  desvioPct: number | null;
  participacaoPct: number | null;
}

const arred1 = (v: number): number => Math.round(v * 10) / 10;

/** Os meses do período, na ordem do payload (snapshot antigo não tem o bloco: lista vazia). */
export function resumoPorLinhaDoPeriodo(resumos: SopResumoLinhaMes[] | undefined, meses: string[]): SopResumoLinhaMes[] {
  return (resumos ?? []).filter((r) => meses.includes(r.mes));
}

const ORDEM_TIPO: Record<TipoLinhaSop, number> = { fabrica: 0, linha: 1, sopro: 2 };

/** As colunas na ordem da tela: a fábrica, as linhas de produto (na ordem em que aparecem), o sopro. */
export function colunasDasLinhas(resumos: SopResumoLinhaMes[]): ColunaLinha[] {
  const colunas: ColunaLinha[] = [];
  resumos.forEach((r) =>
    r.linhas.forEach((l) => {
      if (!colunas.some((c) => c.nome === l.linha)) colunas.push({ nome: l.linha, tipo: l.tipo });
    }),
  );
  return colunas
    .map((c, i) => ({ c, i }))
    .sort((a, b) => ORDEM_TIPO[a.c.tipo] - ORDEM_TIPO[b.c.tipo] || a.i - b.i)
    .map(({ c }) => c);
}

export function nomesDasLinhas(resumos: SopResumoLinhaMes[]): string[] {
  return colunasDasLinhas(resumos).map((c) => c.nome);
}

export function linhaDoMes(r: SopResumoLinhaMes, nome: string): SopTotaisLinha | undefined {
  return r.linhas.find((l) => l.linha === nome);
}

function somarLinha(resumos: SopResumoLinhaMes[], { nome, tipo }: ColunaLinha): Omit<TotalLinha, 'participacaoPct'> {
  let produzido = 0;
  let prodBase = 0;
  let programado = 0;
  let progBase = 0;
  resumos.forEach((r) => {
    const l = linhaDoMes(r, nome);
    if (!l) return;
    produzido += l.produzido;
    programado += l.programado;
    if (r.futuro) return;
    prodBase += produzidoBase(l);
    progBase += l.programado_base;
  });
  return {
    linha: nome,
    tipo,
    produzido,
    produzidoBase: prodBase,
    programado,
    programadoBase: progBase,
    desvioPct: progBase > 0 ? arred1(((prodBase - progBase) / progBase) * 100) : null,
  };
}

/** A soma do período por linha; a participação (só das linhas de produto) é sobre o produzido da fábrica. */
export function totalDoPeriodoPorLinha(resumos: SopResumoLinhaMes[]): TotalLinha[] {
  const totais = colunasDasLinhas(resumos).map((c) => somarLinha(resumos, c));
  const fabrica = totais.find((t) => t.tipo === 'fabrica')?.produzido ?? 0;
  return totais.map((t) => ({
    ...t,
    participacaoPct: t.tipo === 'linha' && fabrica > 0 ? arred1((t.produzido / fabrica) * 100) : null,
  }));
}

/** Marca de ordem de bytes: o Excel só lê UTF-8 (acentos) com ela. */
const BOM = '\uFEFF';

const nfCsv = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1, useGrouping: false });
const numCsv = (v: number | null | undefined): string => (v === null || v === undefined ? '' : nfCsv.format(v));

function celulasDoMes(r: SopResumoLinhaMes, nomes: string[]): string[] {
  return nomes.flatMap((nome) => {
    const l = linhaDoMes(r, nome);
    if (!l) return ['', '', '', ''];
    return [numCsv(produzidoBase(l)), numCsv(l.programado_base), numCsv(l.desvio_pct), numCsv(l.participacao_pct)];
  });
}

/**
 * CSV para o Excel (BOM + `;`, decimal com vírgula, sem separador de milhar), igual ao dos
 * apontamentos. Mesmos números da tela: no mês aberto, só as OPs dos dias fechados.
 */
export function csvPorLinha(resumos: SopResumoLinhaMes[], periodos: Record<string, string | undefined>): string {
  const nomes = nomesDasLinhas(resumos);
  const cabecalho = [
    'Mês',
    'Período',
    'Em andamento',
    ...nomes.flatMap((n) => [`${n} produzido`, `${n} programado`, `${n} desvio %`, `${n} % da fábrica`]),
  ];
  const linhas = resumos.map((r) => [
    r.label,
    periodos[r.mes] ?? '',
    r.em_andamento ? 'sim' : 'não',
    ...celulasDoMes(r, nomes),
  ]);
  return `${BOM}${[cabecalho, ...linhas].map((l) => l.join(';')).join('\r\n')}\r\n`;
}
